import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SearchDoc } from '@shared/index';
import { DocumentChunk } from '@reflact/docsearch';
import { DocumentTocEntry, ExtractionConfig } from '@shared/index';
import { firstValueFrom } from 'rxjs';
import { CounterService } from '@shared-angular/services/counter.service';
import { RouteShareService } from './route-share.service';


export type SelectableDocument = {
  "_id": string,
  "projectId": string,
  "chunkCount": number,
  "description": string,
  "docSearchId": string,
  "filename": string,
  "skillId": string,
  "functionName": string,
  "uploadDate": string,
  "projectName": string,
  "enabledForKnowledge": boolean,
  "publiclyReachable": boolean,
  "uploadedBy": string,
  "mimetype": string,
  "source"?: {
    "type": "upload" | "website" | "sharepoint-file" | "sharepoint-page",
    "id"?: string,
    "path"?: string,
    "url"?: string,
    "urls"?: string[],
  },
  "autoUpdate"?: {
    "enabled": boolean,
    "intervalDays"?: number,
    "disabledReason"?: "manual" | "source_deleted",
    "lastSyncAt"?: string,
    "lastSyncStatus"?: "success" | "failed" | "source_deleted",
    "lastSyncError"?: string,
  },
  "sharepoint"?: {
    "sourceDeletedAt"?: string,
    "lastSourceModifiedAt"?: string,
  },
  "website"?: {
    "lastAutoUpdateAt"?: string,
  },
  "extractionStatus"?: "uploaded" | "extracting" | "extracted" | "failed",
  "extractionConfig"?: ExtractionConfig,
  "extractionError"?: string,
  "extractionErrorType"?: string,
  "tableOfContents"?: DocumentTocEntry[],
  usedIn: {
    "_id": string,
    "docId": string,
    "botId": string,
    "docDescription": string,
    "projectId": string,
    "projectName": string,
    "skillId": string,
    "functionName": string,
    "enabledForKnowledge": boolean,
    "uploadedBy": string,
    "botName": string,
    "botCreator": string,
    "botCreatorName": string
  }[];
};

/** Project document offered for adding to a bot (`botdoc/selectabledocs`). */
export type SelectableProjectDocument = Pick<SelectableDocument, '_id' | 'filename' | 'mimetype' | 'docSearchId'>;

export type MdPreview = {
  status: string;
  markdown: string;
};

export type MsgResponse = {
  status: "error";
  message?: string;
  errorType?: string;
}

export type BotDocUploadResult = {
  status: "ok" | "error";
  message?: string;
  projectDoc?: SelectableDocument;
  botDoc?: SelectableDocument;
};

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  public allowedFileTypes: string[] = [];

  constructor(
    private httpClient: HttpClient,
    private counter: CounterService,
    public rss: RouteShareService
  ) { }


  public async getSelectableDocuments(botId: string) {
    const result = await firstValueFrom(this.httpClient.get<SelectableProjectDocument[]>("/api/toolbox/bot/admin/botdoc/selectabledocs/" + botId));
    return result;
  }


  public async addFileFromProject(fileId: string, botId: string) {
    try {
      const res = await firstValueFrom(this.httpClient.get<{ _id: string }>(`/api/toolbox/bot/admin/botdoc/selectfromproject/${botId}/${fileId}`));
      this.counter.addFile(botId);
      this.counter.setTotalFiles(botId, this.counter.getTotalFiles(botId) + 1);
      this.rss.selectableDocuments = await this.getSelectableDocuments(botId);

      return res;
    } catch (error) {
      console.error('Error while deleting file', error);
      return false;
    }
  }

  public async removeFile(fileId: string, botId: string) {
    try {
      const res = await firstValueFrom(this.httpClient.delete(`/api/toolbox/bot/admin/botdoc/remove/${botId}/${fileId}`));
      await this.getFiles(botId);
      this.rss.selectableDocuments = await this.getSelectableDocuments(botId);
      return res;
    } catch (error) {
      console.error('Error while deleting file', error);
      return false;
    }
  }

  public async getFiles(botId: string): Promise<{ docs: SearchDoc[] }> {
    try {
      const res = await firstValueFrom(this.httpClient.get<{ docs: SearchDoc[] }>(`/api/toolbox/bot/admin/botdoc/files/${botId}`));
      this.rss.documents = res.docs;
      this.counter.setFiles(botId, res.docs.filter((file) => file.enabledForKnowledge).length);
      this.counter.setTotalFiles(botId, res.docs.length);
      return res;
    } catch (error) {
      console.error('Error while fetching files', error);
      return { docs: [] };
    }
  }

  public async changeFileProperties(
    fileId: string,
    state: boolean,
    skillId: string,
    name: string,
    description: string,
    botId: string,
    changedStatus: boolean,
    publiclyReachable: boolean,
    chunkDisplayConfig?: {
      showFilename: boolean,
      showPageNumber: boolean,
      showScore: boolean
    },
    searchType?: "vector" | "text" | "combined",
    docSearchMemoryEnabled?: boolean,
    docSearchMemoryLimit?: number
  ): Promise<SearchDoc | MsgResponse> {
    try {
      if (changedStatus) {
        if (state) {
          this.counter.addFile(botId);
        } else {
          this.counter.removeFile(botId);
        }
      }

      return await firstValueFrom(
        this.httpClient.post<SearchDoc>(
          `/api/toolbox/bot/admin/botdoc/update/${fileId}`,
          {
            enabledForKnowledge: state,
            skillId,
            name,
            description,
            publiclyReachable,
            chunkDisplayConfig,
            searchType,
            docSearchMemoryEnabled,
            docSearchMemoryLimit
          }
        )
      );
    } catch (error) {
      console.error('Error while changing file state', error);
      const httpError = (error as { error?: { message?: string; errorType?: string } }).error;
      return { status: 'error', message: httpError?.message ?? undefined, errorType: httpError?.errorType };
    }
  }

  public async getFileContents(fileId: string): Promise<string> {
    try {
      const result = await firstValueFrom(this.httpClient.get(`/api/toolbox/bot/admin/botdoc/getfile/${fileId}`, { responseType: 'text' }));
      return result;
    } catch (error) {
      console.error('Error while changing file state', error);
      return "unable to fetch file contents";
    }
  }

  public async getFileChunks(botId: string, docSearchId: string): Promise<DocumentChunk[]> {
    try {
      const result = await firstValueFrom(this.httpClient.get<DocumentChunk[]>(`/api/toolbox/bot/admin/botdoc/chunks/${botId}/${docSearchId}`));
      return result;
    } catch (error) {
      console.error('Error while changing file state', error);
      return [];
    }
  }

  public async getFileForDownload(fileId: string) {
    try {
      const result = await firstValueFrom(this.httpClient.get(`/api/toolbox/bot/admin/botdoc/downloadfile/${fileId}`, { responseType: 'arraybuffer' }));
      return result;
    } catch (error) {
      console.error('Error while changing file state', error);
      return "unable to fetch file contents";
    }
  }

  public async getToolConfigPreview(botId: string, toolId: string) {
    try {
      return await firstValueFrom(this.httpClient.get<MdPreview>(`/api/toolbox/bot/admin/toolpreview/${botId}/${toolId}`));
    } catch (error) {
      console.error('Error while fetching tool config preview', error);
      return { 'status': 'nok', 'markdown': 'unable to fetch file contents' };
    }
  }

  public async loadAllowedTypes(): Promise<void> {
    try {
      const result = (await firstValueFrom(this.httpClient.get<{ allowedTypes: string[] }>(`/api/toolbox/bot/admin/botdoc/allowedtypes`)));
      this.allowedFileTypes = result.allowedTypes;
    } catch (error) {
      console.error('Error while fetching allowed file types', error);
      this.allowedFileTypes = [];
    }
  }

  public async uploadProjectDocument(file: File, projectId: string, botId?: string): Promise<BotDocUploadResult> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);
      if (botId) {
        formData.append('botId', botId);
      }

      const result = await firstValueFrom(this.httpClient.post<BotDocUploadResult>(`/api/toolbox/bot/admin/botdoc/upload`, formData));
      this.rss.selectableDocuments = await this.getSelectableDocuments(botId ?? this.rss.botConfig._id);
      return result;
    } catch (error: unknown) {
      console.error('Error while uploading file', error);
      return {
        status: 'error',
        message: (error as { error?: { message?: string } }).error?.message ?? 'Das Hochladen ist fehlgeschlagen. Versuche es erneut.'
      };
    }
  }
}